import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import "../../styles/AppOrgSection.css";

// --- Components ---
import ApplicationCard from "./ApplicationCard";
import LockedApplicationCard from "./LockedApplicationCard";
import AddApplicationModal from "./AddApplicationModal";
import CloneApplicationModal from "./CloneApplicationModal";
import SearchQuery from "../../components/BuilderComponents/SearchQuery";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import AssignUserToApplication from "./AssignUserToApplication";

import { useAxiosContext } from "../../context/AxiosContext";
import { useModalContext } from "../../context/ModalContext";
import { useAppOrgContext } from "../../context/AppOrgContext";
import { useTreeContext } from "../../context/TreeDataContext";
import { BASE_URL } from "../../constant/constant";

import {
  createApplicationApi,
  updateApplicationApi,
  deleteApplicationApi,
  lockApplicationApi,
  unlockApplicationApi,
  createInfraApi,
  deployInfraApi,
  destroyInfraApi,
  cloneApplicationApi
} from "../../services/apis/applicationApis";
import { toastSuccess } from "../../utils/toastUtils";
import { handleApiError } from "../../services/axiosService";

const sortAppListByStatus = (apps, priority = "live") =>
  [...apps].sort((a, b) => {
    if (a.status === priority && b.status !== priority) return -1;
    if (a.status !== priority && b.status === priority) return 1;
    return String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase());
  });

const ApplicationView = ({ applications, lockedAppsMap, setLockedAppsMap }) => {
  const params = useParams();
  const navigate = useNavigate();

  const { setApplicationId } = useAxiosContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { setAllApplications, appOrgNames, setAppOrgnames, setApplicationUrl, setApplication } = useAppOrgContext();
  const { setScreensExplorerData, setSelectedDevice } = useTreeContext();

  const [selectedApplication, setSelectedApplication] = useState("");
  const [modal, setModal] = useState({ type: null, data: null });
  const [filteredApplications, setFilteredApplications] = useState([]);

  const sortedApplications = useMemo(() => sortAppListByStatus(applications || []), [applications]);

  const displayedApplications =
    filteredApplications.length > 0 || filteredApplications.length === 0 ? filteredApplications : sortedApplications;

  const toggleAppLock = async (appId, isLocked) => {
    try {
      if (isLocked) {
        await unlockApplicationApi({ appId });
        setLockedAppsMap((prev) => {
          const next = { ...prev };
          delete next[appId];
          return next;
        });
        toastSuccess("Application unlocked successfully");
      } else {
        const { data } = await lockApplicationApi({ appId });
        setLockedAppsMap((prev) => ({ ...prev, [appId]: data }));
        toastSuccess("Application locked successfully");
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const addApplication = async (payload) => {
    try {
      const { data, message } = await createApplicationApi({
        orgId: params.orgId,
        payload
      });

      setAllApplications((prev) => [...prev, data]);
      setModal({ type: null });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const editApplication = async (payload) => {
    try {
      const { message } = await updateApplicationApi({
        orgId: params.orgId,
        payload
      });

      setAllApplications((prev) => prev.map((app) => (app.id === payload.id ? { ...app, ...payload } : app)));

      setModal({ type: null });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const deleteApplication = async (id, name) => {
    const confirmed = await getUserConfirmationForDelete(name, "app");
    if (!confirmed) return;

    try {
      const { message } = await deleteApplicationApi({
        orgId: params.orgId,
        appId: id
      });

      setAllApplications((prev) => prev.filter((app) => app.id !== id));
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleInfraAction = async (action, type, appId) => {
    try {
      if (action === "create") {
        await createInfraApi({ appId, type });
      }

      if (action === "deploy") {
        await deployInfraApi({ appId, type });
      }

      if (action === "destroy") {
        await destroyInfraApi({ appId, type });
      }

      toastSuccess("Action completed successfully");
    } catch (error) {
      handleApiError(error);
    }
  };

  const destroyAction = async (type, id, name) => {
    const confirmed = await getUserConfirmationForDelete(name, "", "stop");
    if (confirmed) {
      handleInfraAction("destroy", type, id);
    }
  };

  // -------------------- Navigation --------------------
  const openApp = (app, statusData) => {
    setApplication(app);
    setApplicationUrl(statusData);
    setApplicationId(app.id);
    setScreensExplorerData(false);
    setSelectedDevice("desktop");
    setAppOrgnames((prev) => ({ ...prev, appName: app.name }));
    navigate(`./${app.id}/screen`);
  };

  const openAppAddModal = () => setModal({ type: "app", data: { description: "", name: "", logo: "" } });

  const openAppEditModal = (e, app) => {
    e?.stopPropagation();
    setModal({ type: "app", data: app });
  };

  const openAppCloneModal = () => setModal({ type: "clone" });

  const getModalName = () => {
    if (modal.type === "app") {
      return modal.data?.id ? "Edit Application" : "Add Application";
    }
    if (modal.type === "user") return "Assign Users";
    if (modal.type === "clone") return "Clone Application";
    return "";
  };

  // -------------------- Render --------------------
  return (
    <div className="application_container">
      <AppLeftSideBar type="app" />

      <div className="dashboard_outer_section">
        <div className="dashboard-top-header">
          {params.orgId && appOrgNames.orgName && (
            <div className="d_flex align_center gap_2">
              {appOrgNames?.orgLogo ? (
                <img
                  src={`${BASE_URL}/api/v1/public/${appOrgNames.orgLogo}`}
                  alt="logo"
                  style={{ width: "40px", objectFit: "contain" }}
                />
              ) : (
                <MaterialIcon name="deployed_code" />
              )}
              <span className="org_app_title">{appOrgNames.orgName}</span>
            </div>
          )}

          <SearchQuery
            data={sortedApplications}
            searchKey="name"
            onSearch={setFilteredApplications}
            placeholder="Search Applications"
          />
        </div>

        {/* Body */}
        <div className="dashboard_page_body org-dashboard-page-body-grid">
          {/* Clone App */}
          <div className="add_item_box" onClick={openAppCloneModal}>
            <MaterialIcon name="move_group" className="builder_icon" />
            <h2 className="add_app_title">Clone App</h2>
          </div>

          {/* Add App */}
          <div className="add_item_box" onClick={openAppAddModal}>
            <MaterialIcon name="add" className="builder_icon" />
            <h2 className="add_app_title">Add new app</h2>
          </div>

          {/* Applications */}
          {displayedApplications.length ? (
            displayedApplications.map((app) => {
              const lockData = lockedAppsMap[app.id];

              if (lockData) {
                return (
                  <LockedApplicationCard
                    key={app.id}
                    app={app}
                    lockData={lockData}
                    onUnlock={() => toggleAppLock(app.id, true)}
                    openApp={openApp}
                  />
                );
              }

              return (
                <ApplicationCard
                  key={app.id}
                  app={app}
                  onLock={() => toggleAppLock(app.id, false)}
                  deleteApplication={() => deleteApplication(app.id, app.name)}
                  openAppEditModal={(e) => openAppEditModal(e, app)}
                  createAction={(type) => handleInfraAction("create", type, app.id)}
                  deployAction={(type) => handleInfraAction("deploy", type, app.id)}
                  destroyAction={(type) => destroyAction(type, app.id, app.name)}
                  openApp={openApp}
                  setSelectedApplication={setSelectedApplication}
                  setIsModalOpen={(type) => setModal({ type, data: app })}
                />
              );
            })
          ) : (
            <div className="no_applications_found">
              <p className="search_result_text">No Applications Found</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <BuilderModal isOpen={!!modal.type} onClose={() => setModal({ type: null })} modalName={getModalName()}>
        {modal.type === "app" && (
          <AddApplicationModal
            editData={modal.data}
            addApplication={addApplication}
            editApplication={editApplication}
          />
        )}

        {modal.type === "user" && <AssignUserToApplication appId={selectedApplication} orgId={params.orgId} />}

        {modal.type === "clone" && (
          <CloneApplicationModal
            editData={{ source_app_id: "", target_org_id: params.orgId }}
            addApplication={({ source_app_id }) =>
              cloneApplicationApi({
                sourceAppId: source_app_id,
                targetOrgId: params.orgId
              })
            }
          />
        )}
      </BuilderModal>
    </div>
  );
};

export default ApplicationView;
